// @ts-nocheck
import { escapeXMLAttributeValue, escapeXMLCharacterData, interactivityRegistration, openUrl, setHot, setNodeClassEnabled, shouldHandleMouseClick } from "./Charta";
import { DomPathQuery, DomQuery, HtmlClassCounter, HtmlClassSwitch, HtmlClasses, WithClass, WithTagName, nodeHasClass } from "./Dom";
import { Enumeration } from "./Enumeration";
import { CountChangedEvent, DataChangedEvent, distributeEventUpHierarchy, getEvent } from "./Event";
import { Timer } from "./Timer";
import { application } from "./WebApp.Instance";
import { createPopUp } from "./WebApp.PopUp";
import { QueueSizeChangedEvent, RequestQueue, XmlMessage } from "./WebApp.Request.Queue";
import { ToastMessage } from "./WebApp.ToastBox";
import { CheckBoxField } from "./WebSite.Form.Field.Boolean.CheckBox";
import { TableRow } from "./WebSite.Page.Component.Table.Row";
import { Toolbar, ToolbarState } from "./WebSite.Page.Component.Toolbar";
import { ComponentWithName, Labels, RefreshMode, WebPageComponent, connectClickOutsideListener, removeClickOutsideListener } from "./WebSite.Page.Component";

var TableRenderScope = new Enumeration(["Toolbar", "Layout", "ActiveFilters", "Header", "Rows", "Footer", "Complete", "Navigation"]);

function ColumnMapping(identifier, index, span) {
  this.identifier = identifier;
  this.index = index;
  this.span = span;
}

export class CommandMessage extends XmlMessage {
  constructor() {
    super();

    this.commands = new Array();
  }

  toXml() {
    const result = this.commands.join("");
    return `<Commands>${result}</Commands>`;
  }
}

export class RenderMessage extends XmlMessage {
  constructor(scopes) {
    super();

    this.scopes = scopes;
  }

  toXml() {
    let command = "<Render>"

    this.scopes.forEach(
      (scope) => {
        command = command + "<Scope Name=\"" + TableRenderScope.toText(scope) + "\"/>";
      }
    );

    command += "</Render>";
    return command;
  }
}

export class Table extends WebPageComponent {
  constructor(element) {
    super(element);

    this.element.tabIndex = "0";

    this.loadSettings(this.element);
    this.determineElements();
    this.initializeComponents();
    this.initializeCells();

    this.attachEventHandlers();
    this.attachNavigationHandlers();
    this.attachSearchHandler();
    this.attachMenuEventHandlers();
    this.attachViewHandlers();
    this.attachTableEventHandlers();
    this.attachActiveFiltersHandlers();
  }

  getTableElement() {
    return this.tableElement;
  }

  getRows() {
    return this.getTableElement().tBodies[0].rows;
  }

  getForm() {
    const form = this.tableElement.parentNode;

    if (form.tagName === "FORM")
      return form;
    else
      return null;
  }

  determineTableElement() {
    let parent = this.contents;

    if (this.useSiblingComponent)
      parent = new DomQuery(parent).getNthChild(WithTagName("FORM"), 0);

    return new DomQuery(parent).getChild(WithTagName("TABLE"));
  }

  columnClicked(columnIdentifier) {
    this.sendCommandRequest("<SortColumn Id=\"" + columnIdentifier + "\"/>");
  }

  updateSortColumn(element) {
    const columnIdentifier = element.getAttribute("ColumnIdentifier");
    const order = element.getAttribute("Order");

    for (const row of this.getColumnsRows()) {
      for (const cell of row.cells) {
        const id = cell.dataset.Identifier;
        const icon = new DomQuery(cell).getDescendant(WithClass("Sort"));

        if (icon !== null) {
          const classes = new HtmlClasses(icon);

          classes.remove("Ascending");
          classes.remove("Descending");

          if (id === columnIdentifier) {
            classes.add(order == 0 ? "Ascending" : "Descending");
          }
        }
      }
    }
  }

  getHeaderRowWithClass(className) {
    return Array.from(this.getTableElement().tHead.rows).find(row => nodeHasClass(row, className)) || null;
  }

  getHeaderRowsWithClass(className) {
    return Array.from(this.getTableElement().tHead.rows).filter(row => nodeHasClass(row, className));
  }

  getSearchRow() {
    return new DomQuery(this.toolbar.element).getDescendant(WithClass("Search"));
  }

  getSearchInput() {
    const row = this.getSearchRow();

    if (row !== null)
      return new DomQuery(row).getDescendant(WithTagName("INPUT"));
    else
      return null;
  }

  getFilterRows() {
    return this.getHeaderRowsWithClass("Filter");
  }

  getColumn(identifier) {
    const columnGroup = this.tableElement.firstChild;
    let column = null;

    for (const node of columnGroup.childNodes)
      if (node.dataset.Identifier === identifier)
        column = node;

    return column;
  }

  getColumnsRows() {
    return this.getHeaderRowsWithClass("Columns");
  }

  toggleFilter() {
    this.filterEnabled = !this.filterEnabled;
    this.sendCommandRequest("<Filter Visible=\"" + this.filterEnabled + "\"/>");
    this.updateFilterVisibility();
  }

  filterColumn(column, mode, expression) {
    this.filterEnabled = true;
    this.updateFilterVisibility();
    this.sendCommandRequest(
      "<Filter Visible=\"true\"/>" +
      "<FilterColumn Id=\"" + escapeXMLAttributeValue(column) + "\" Mode=\"" + escapeXMLAttributeValue(mode) + "\" Text=\"" + escapeXMLAttributeValue(expression) + "\"/>"
    );
  }

  setView(view) {
    if (view !== this.element.dataset.View) {
      this.element.dataset.View = view;
      this.sendCommandRequest("<View Value=\"" + this.element.dataset.View + "\"/>");
    }
  }

  updateFilterVisibility() {
    const rows = this.getFilterRows();

    for (const row of rows)
      setNodeClassEnabled(row, "Enabled", this.filterEnabled);
  }

  toggleLayoutPanel() {
    this.layoutPanel.open();
  }

  scheduleFilterOperation(operation) {
    this.filterTimer.kill();
    this.filterTimer.schedule(operation);
  }

  searchTextChanged(textBox) {
    if (textBox.value !== this.filterText) {
      this.filterText = textBox.value;
      this.scheduleFilterOperation(() => this.sendCommandRequest("<Search Text=\"" + escapeXMLAttributeValue(textBox.value) + "\"/>"));
    }
  }

  sendCommandRequest(command, blocking = true) {
    let message = null;

    if (this.requestQueue.getSize() >= 2) {
      const request = this.requestQueue.getLast();

      if (request.message instanceof CommandMessage) {
        message = request.message;
        message.commands.push(command);

        request.blocking = request.blocking || blocking;
      }
    }

    if (message === null) {
      message = new CommandMessage();
      message.commands.push(command);

      this.requestQueue.queue(
        this.uri,
        "POST",
        message,
        null,
        async (response) => {
          if (response.ok)
            await this.handleHttpCommandResponse(response);
        },
        blocking
      );
    }
  }

  async handleHttpCommandResponse(response) {
    const contentType = response.headers.get("Content-Type");

    if (contentType.slice(0, 15) == "application/xml") {
      const responseText = await response.text();
      const xml = new DOMParser().parseFromString(responseText, "application/xml");

      if (xml.documentElement.hasAttribute("Location")) {
        const type = xml.documentElement.getAttribute("Type");
        const redirect = xml.documentElement.getAttribute("Location");

        if (type === "Download") {
          window.open(redirect);
          this.refresh();
        }
        else if (type === "Redirect") {
          if (!application.pageHandler.navigate(redirect))
            this.refresh();
        }
      }
      else {
        const query = new DomQuery(xml.documentElement);
        const cursorElement = query.getChild(WithTagName("Cursor"));

        if (cursorElement !== null) {
          this.navigation.update(cursorElement);
          this.fireCountChangedEvent();
        }

        const scopesElement = query.getChild(WithTagName("Scopes"));
        const scopeElements = new DomQuery(scopesElement).getChildren(WithTagName("Scope"));

        const scopes = new Array();

        for (const scope of scopeElements)
          scopes.push(TableRenderScope.fromText(scope.getAttribute("Name")));

        this.invalidateScopes(scopes);
      }
    }
    else if (contentType.slice(0, 9) === "text/html")
      this.handleHttpRenderResponse(response);
  }

  sendActionRequest(action, form, callback) {
    let formData;

    if (form !== null)
      formData = new FormData(form);
    else
      formData = new FormData();

    formData.append("Action", action);
    formData.append("Selection", this.createSelectionElement());

    this.requestQueue.queue(
      this.uri,
      "POST",
      null,
      formData,
      async (response) => {
        if (response.ok) {
          await this.handleHttpCommandResponse(response);

          this.fireDataChanged();
          application.toastBox.addMessage(new ToastMessage(this.labels.ActionExecutedSuccessfully, "Success"));
        }
        else if (response.status === 400) {
          this.toolbar.state = ToolbarState.Warning;
          await this.refreshFromResponse(response);
          this.fireDataChanged();
        }
        else
          this.requestQueue.handleErrorResponse(response);

        callback();
      }
    );
  }

  sendRenderRequest(scopes) {
    this.requestQueue.queue(
      this.uri,
      "POST",
      new RenderMessage(scopes),
      null,
      async (response) => await this.handleHttpRenderResponse(response)
    );
  }

  handleHttpRenderScopeResponse(element, scope) {
    if (scope === TableRenderScope.Toolbar)
      this.replaceToolbar(element.firstChild, true);
    else if (scope === TableRenderScope.Layout)
      this.replaceLayoutPanel(element.firstChild.firstChild);
    else if (scope === TableRenderScope.Header)
      this.replaceHeader(element.firstChild.tHead, element.firstChild.firstChild);
    else if (scope === TableRenderScope.Rows)
      this.replaceBody(element.firstChild.firstChild);
    else if (scope === TableRenderScope.Footer)
      this.replaceFooter(element.firstChild.tFoot);
    else if (scope === TableRenderScope.Navigation)
      this.replaceNavigation(element.firstChild)
    else if (scope === TableRenderScope.ActiveFilters)
      this.replaceActiveFilters(element.firstChild)
    else if (scope === TableRenderScope.Complete) {
      const newElement = element.firstChild;
      const newToolbar = new DomQuery(newElement).getChild(WithClass("Toolbar"));
      const newLayoutPanel = new DomQuery(newElement).getChild(WithClass("Layout"));
      const newActiveFilters = new DomQuery(newElement).getChild(WithClass("ActiveFilters"));
      const newTable = new DomQuery(newElement).getChild(WithClass("Contents")).firstChild;

      this.loadSettings(newElement);

      this.replaceTable(newTable);
      this.replaceToolbar(newToolbar, false);
      this.replaceLayoutPanel(newLayoutPanel);
      this.replaceActiveFilters(newActiveFilters);
    }
  }

  async handleHttpRenderResponse(response) {
    const dummyElement = document.createElement("div");
    dummyElement.innerHTML = await response.text();

    for (const element of dummyElement.firstChild.childNodes) {
      const scope = TableRenderScope.fromText(element.className);
      this.handleHttpRenderScopeResponse(element, scope);
    }

    this.renderStatus = true;
    this.updateFilterVisibility();

    if (this.selectAll !== null)
      this.selectAll.recalculate(this);

    this.getSize();
  }

  invalidateScopes(scopes) {
    for (const scope of scopes)
      this.invalidScopes.add(scope);

    if (this.invalidScopes.size !== 0) {
      this.sendRenderRequest(new Set(this.invalidScopes));
      this.invalidScopes.clear();
    }
  }

  replaceHeader(header, columnGroup) {
    interactivityRegistration.detach(this.tableElement.tHead);
    this.tableElement.replaceChild(header, this.tableElement.tHead);
    this.tableElement.replaceChild(columnGroup, this.tableElement.childNodes[0])
    interactivityRegistration.attach(header);

    this.createSelector();

    this.attachTableEventHandlers();
    this.fireChangedEvent(this.tableElement);
  }

  replaceFooter(footer) {
    if (footer !== null)
      this.tableElement.replaceChild(footer, this.tableElement.tFoot);
  }

  replaceNavigation(navigation) {
    this.element.replaceChild(navigation, this.navigation.element);

    this.navigation = new Navigation(navigation);
    this.attachNavigationHandlers();
  }

  replaceActiveFilters(activeFilters) {
    if (activeFilters !== null && this.activeFilters !== null) {
      this.element.replaceChild(activeFilters, this.activeFilters);
      this.activeFilters = activeFilters;

      this.attachActiveFiltersHandlers();
    }
  }

  attachActiveFiltersHandlers() {
    if (this.activeFilters !== null) {
      for (const activeFilter of new DomQuery(this.activeFilters).getChildren(WithClass("FilterValue"))) {
        const close = new DomQuery(activeFilter).getChild(WithClass("Close"));

        if (close !== null)
          close.addEventListener("click", (event) => this.clearColumnFilter(activeFilter.dataset.Identifier, activeFilter.dataset.Mode));
      }

      const clearFilters = new DomQuery(this.activeFilters).getChild(WithClass("ClearFilters"));

      if (clearFilters !== null)
        clearFilters.addEventListener("click", (event) => this.clearAllFilters());
    }
  }

  clearColumnFilter(column, mode) {
    this.sendCommandRequest("<FilterColumn Id=\"" + escapeXMLAttributeValue(column) + "\" Mode=\"" + escapeXMLAttributeValue(mode) + "\" Text=\"\"/>");
    this.invalidateScopes([TableRenderScope.Header]);
  }

  clearAllFilters() {
    let command = "";

    for (const activeFilter of new DomQuery(this.activeFilters).getChildren(WithClass("FilterValue")))
      command = command + "<FilterColumn Id=\"" + escapeXMLAttributeValue(activeFilter.dataset.Identifier) + "\" Mode=\"" + escapeXMLAttributeValue(activeFilter.dataset.Mode) + "\" Text=\"\"/>";

    this.sendCommandRequest(command);
    this.invalidateScopes([TableRenderScope.Header]);
  }

  attachEventHandlers() {
    this.element.addEventListener(
      "keydown",
      (event) => {
        // TODO: Verifying if the action form is enabled is a quick fix that should be removed once it is moved out of the table's dom element.
        if (!this.toolbar.hasActionEnabled() && !this.tableElement.contains(event.target) && event.code === "ArrowDown") {
          const rows = this.getRows();

          if (rows.length > 0)
            rows[0].focus();

          event.stopHandling();
        }
        else if (event.code === "F5") {
          this.refresh();
          this.element.focus();
          event.stopHandling();
        }
        else if (event.altKey && event.shiftKey && event.code === "KeyX") {
          this.toggleExpertMode();
          event.preventDefault();
        }
      }
    );
  }

  attachNavigationHandlers() {
    if (this.navigation.nextPage !== null) {
      this.navigation.nextPage.addEventListener("click", (event) => this.moveCursor("Up"));
      this.navigation.previousPage.addEventListener("click", (event) => this.moveCursor("Down"));
    }
    else {
      this.navigation.loadRows.addEventListener("click", (event) => this.setPageSize(this.navigation.increasePageSize(this.navigation.size)));
    }
  }

  moveCursor(direction) {
    this.sendCommandRequest("<CursorMove Direction=\"" + direction + "\"/>");
  }

  getSize() {
    this.sendCommandRequest("<Size/>", false);
  }

  setPageSize(count) {
    this.sendCommandRequest("<Cursor PageSize=\"" + count + "\"/>");
  }

  replaceBody(body) {
    interactivityRegistration.detach(this.tableElement.tBodies[0]);
    this.tableElement.replaceChild(body, this.tableElement.tBodies[0]);
    interactivityRegistration.attach(body);

    this.initializeCells();
    this.attachRowEventHandlers();
    this.fireChangedEvent(this.tableElement);
  }

  replaceTable(table) {
    interactivityRegistration.detach(this.contents.firstChild);
    this.contents.replaceChild(table, this.contents.firstChild);
    this.tableElement = this.determineTableElement();

    interactivityRegistration.attach(table);

    this.initializeCells();
    this.attachRowEventHandlers();
    this.createSelector();
    this.attachTableEventHandlers();
    this.fireChangedEvent(this.tableElement);
  }

  replaceToolbar(toolbar, persistForm) {
    this.toolbar.replace(toolbar, persistForm);

    if (this.toolbar.hasActionEnabled())
      this.element.classList.add("ActionEnabled");
    else
      this.element.classList.remove("ActionEnabled");

    this.attachMenuEventHandlers();
    this.attachViewHandlers();
  }

  replaceLayoutPanel(layoutPanel) {
    interactivityRegistration.detach(this.layoutPanel.element);
    this.element.replaceChild(layoutPanel, this.layoutPanel.element);
    interactivityRegistration.attach(layoutPanel);
    this.layoutPanel = new LayoutPanel(layoutPanel, this);
  }

  fireChangedEvent(newTable) {
    if (this.onChanged !== null)
      this.onChanged(newTable);
  }

  fireCountChangedEvent() {
    distributeEventUpHierarchy(new CountChangedEvent(this.navigation.total), this);
  }

  selectRow(checkBox) {
    const tableRow = new DomQuery(checkBox).getAncestor(WithTagName("TR"));
    setNodeClassEnabled(tableRow, "selectedRow", checkBox.checked);
  }

  selectAllRows(checked) {
    this.selectRange(0, this.getRows().length - 1, checked);
  }

  selectRange(low, high, checked) {
    const rows = this.getRows();

    for (let index = low; index <= high; index++) {
      const row = rows[index];
      const cell = row.cells[0];
      const checkBoxToChange = cell.getElementsByTagName("input")[0];

      if (checkBoxToChange != null && checkBoxToChange.checked !== checked && !checkBoxToChange.disabled) {
        checkBoxToChange.checked = checked;
        this.selectRow(checkBoxToChange);
      }
    }
  }

  createRowHotHandler(row, flag) {
    return function (event) {
      setHot(row, event, flag);
    };
  }

  createRowClickedHandler(row, uri) {
    return function (event) {
      const source = getEvent(event).getSource();

      if (shouldHandleMouseClick(row, source))
        openUrl(event, uri);
    };
  }

  attachRowEventHandlers() {
    for (const row of this.getRows()) {
      const component = new TableRow(row, this);

      if (component.uri !== null) {
        row.addEventListener("mouseover", this.createRowHotHandler(row, true));
        row.addEventListener("mouseout", this.createRowHotHandler(row, false));
        row.addEventListener("click", this.createRowClickedHandler(row, component.uri));
      }

      if (this.cellActionsEnabled)
        this.attachCellActionHandlers(row);
    }
  }

  attachCellActionHandlers(row) {
    for (const cell of row.cells) {
      if (cell.dataset.Column !== undefined && nodeHasClass(cell, "display"))
        new TableCellActions(this, cell);
    }
  }

  determineCellActionsEnabled() {
    return new DomQuery(this.element.parentNode).getAncestor((node) => nodeHasClass(node, "Suggest") || nodeHasClass(node, "PopUp")) === null;
  }

  refresh() {
    this.invalidateScopes([TableRenderScope.Toolbar, TableRenderScope.Header, TableRenderScope.ActiveFilters, TableRenderScope.Rows, TableRenderScope.Footer, TableRenderScope.Navigation]);
  }

  refreshFromResponse(http) {
    this.handleHttpRenderResponse(http);
  }

  attachColumnSortHandlers() {
    let totalSpan = 0;

    for (const row of this.getColumnsRows()) {
      for (const cell of row.cells) {
        const classes = new HtmlClasses(cell);

        if (classes.contains("Column")) {
          const columnIdentifier = cell.dataset.Identifier;

          const caption = new DomQuery(cell).getDescendant(WithClass("Caption"));
          caption.classList.add("Action");

          caption.addEventListener(
            "click",
            (event) => {
              this.columnClicked(columnIdentifier);
            }
          );

          this.columnIdentifierIndexMapping[columnIdentifier] = new ColumnMapping(columnIdentifier, totalSpan, cell.colSpan);
          totalSpan += cell.colSpan;
        }
      }
    }
  }

  resizeColumn(columnIdentifier, delta) {
    const column = this.getColumn(columnIdentifier);

    const reference = document.createElement("span");
    reference.innerHTML = "0";
    reference.style.visibility = "hidden";
    reference.style.fontSize = window.getComputedStyle(this.tableElement.tBodies[0]).fontSize;
    reference.style.fontWeight = "bold";
    reference.style.display = "block"
    reference.style.width = "min-content"

    document.body.appendChild(reference);
    const ratio = parseFloat(window.getComputedStyle(reference).width);
    document.body.removeChild(reference);

    const width = Math.max(parseFloat(column.style.width) + delta / ratio, 6);
    column.style.width = width + "ch";
  }

  commitColumnSize(columnIdentifier) {
    const column = this.getColumn(columnIdentifier);
    const width = parseFloat(column.style.width);
    const command = "<Resize Id=\"" + columnIdentifier + "\" Width=\"" + width + "\"/>";

    this.sendCommandRequest(command);
  }

  startResize() {
    this.tableElement.style.userSelect = "none";
  }

  finishResize() {
    this.tableElement.style.userSelect = "";
  }

  attachColumnResizeHandler(cell) {
    let pageX, column;

    cell.addEventListener(
      "mousedown",
      (event) => {
        pageX = event.pageX;
        column = event.target;

        this.startResize();
        cell.classList.add("Dragging");
      }
    );

    document.addEventListener(
      "mousemove",
      (event) => {
        if (column !== undefined) {
          if (pageX !== undefined)
            this.resizeColumn(column.dataset.Identifier, event.pageX - pageX);

          pageX = event.pageX;
        }
      }
    );

    document.addEventListener(
      "mouseup",
      (event) => {
        if (column !== undefined) {
          this.commitColumnSize(column.dataset.Identifier);
          event.stopPropagation();

          cell.classList.remove("Dragging");
          this.finishResize();
        }

        pageX = undefined;
        column = undefined;
      }
    );

    cell.onclick = (event) => {
      event.stopPropagation();
    };
  }

  attachColumnResizeHandlers() {
    for (const row of this.getColumnsRows()) {
      for (const cell of row.cells) {
        const resizer = new DomQuery(cell).getDescendant(WithClass("Resizer"));

        if (resizer !== null)
          this.attachColumnResizeHandler(resizer);
      }
    }
  }

  createColumnFilterHandler() {
    return () => {
      this.scheduleFilterOperation(
        () => {
          let command = "";
          let filters = 0;

          for (const field of this.filters) {
            const expression = field.getFilterText();
            const mode = field.getFilterMode();

            if (field.initialFilterText !== expression || field.initialFilterMode !== mode)
              command = command + "<FilterColumn Id=\"" + field.getId() + "\" Mode=\"" + escapeXMLAttributeValue(mode) + "\" Text=\"" + escapeXMLAttributeValue(expression) + "\"/>";

            field.initialFilterText = field.getFilterText();
            field.initialFilterMode = field.getFilterMode();

            const rows = this.getColumnsRows();

            for (const row of rows) {
              for (const cell of row.cells) {
                if (cell.dataset.Identifier === field.getId()) {
                  const icon = new DomQuery(cell).getDescendant(WithClass("Filter"));

                  if (icon !== null) {
                    const classes = new HtmlClasses(icon);

                    if (expression.length > 0) {
                      classes.add("Value");
                      filters++;
                    }
                    else
                      classes.remove("Value");
                  }
                }
              }
            }
          }

          const toggleFilter = new DomQuery(this.element).getDescendant(WithClass("ToggleFilter"));

          if (filters > 0)
            toggleFilter.childNodes[0].innerText = filters;
          else
            toggleFilter.childNodes[0].innerText = "";

          this.sendCommandRequest(command);
        }
      );
    }
  }

  attachSearchHandler() {
    const searchInput = this.getSearchInput();

    if (searchInput !== null) {
      this.filterText = searchInput.value;
      searchInput.addEventListener("input", (event) => this.searchTextChanged(searchInput))
    }
  }

  attachColumnFilterHandlers() {
    this.filters = new Array();

    for (const row of this.getFilterRows()) {
      for (const cell of row.cells) {
        const classes = new HtmlClasses(cell);
        const query = new DomQuery(cell);

        if (classes.contains("Column")) {
          const element = query.getDescendant(WithClass("Box"));
          const id = cell.dataset.Identifier;

          if (element !== null)
            this.filters.push(new FilterField(element, this.createColumnFilterHandler(), id));
        }
      }
    }
  }

  attachMenuEventHandlers() {
    this.attachToolbarButtonEventHandler("ToggleFilter", (event) => { this.toggleFilter(); });
    this.attachToolbarButtonEventHandler("Refresh", (event) => { this.refresh(); });
    this.attachToolbarButtonEventHandler("Layout", (event) => { this.toggleLayoutPanel(); });
  }

  toggleAction(action) {
    this.element.classList.add("ActionEnabled");
    this.sendCommandRequest(this.createSelectionElement() + "<Action Name=\"" + action + "\"/>");
  }

  attachViewHandlers() {
    const view = new DomQuery(this.element).getDescendant(WithClass("View"));

    if (view !== null) {
      const auto = new DomQuery(view).getChild(WithClass("Auto"));
      const list = new DomQuery(view).getChild(WithClass("List"));
      const columns = new DomQuery(view).getChild(WithClass("Columns"));
      const tiles = new DomQuery(view).getChild(WithClass("Tiles"));

      auto.addEventListener("click", (event) => this.setView("Auto"));
      list.addEventListener("click", (event) => this.setView("List"));
      columns.addEventListener("click", (event) => this.setView("Columns"));
      tiles.addEventListener("click", (event) => this.setView("Tiles"));
    }
  }

  attachToolbarButtonEventHandler(action, handler) {
    const query = new DomQuery(this.element);
    const item = query.getDescendant(WithClass(action));

    if (item !== null)
      item.addEventListener("click", handler);
  }

  attachTableEventHandlers() {
    this.attachColumnSortHandlers();
    this.attachColumnFilterHandlers();
    this.attachColumnResizeHandlers();

    if (this.selectAll !== null)
      this.selectAll.checkBox.addEventListener(
        "change",
        (event) => {
          this.selectAllRows(this.selectAll.checkBox.getValue());
          this.fireSelectionChangedEvent();
        }
      );

    this.onSelectionChanged = () => {
      this.sendCommandRequest(this.createSelectionElement());
    }
  }

  createSelectionElement() {
    let result = "<Selection>"
    const selection = this.getSelectedRows();

    for (const row of selection)
      result = result + "<Row>" + escapeXMLCharacterData(row) + "</Row>";

    result = result + "</Selection>";
    return result;
  }

  handleInitialRenderTime() {
    if (this.renderTime === "Immediate") {
      this.renderStatus = true;
      this.getSize();
    }
    else if (this.renderTime === "Deferred")
      this.refresh();
  }

  assureRendered() {
    if (!this.renderStatus)
      this.refresh();
  }

  getSelectedRows() {
    const rows = this.getRows();
    const result = new Array();

    for (const row of rows)
      if (row.tableRow.selected)
        result.push(row.dataset.Identifier);

    return result;
  }

  getSelectedRowCount() {
    const rows = this.getRows();
    let count = 0;

    for (const row of rows)
      if (row.tableRow.selected)
        count++;

    return count;
  }

  collectPublishedActions() {
    const selectedRows = this.collectSelectedRows();
    const candidates = new Map();

    if (selectedRows.length > 0) {
      this.initializeActionCandidates(candidates, selectedRows[0]);

      for (const row of selectedRows.slice(1))
        this.intersectActionCandidates(candidates, row);
    }

    return this.buildActionsFromCandidates(candidates);
  }

  collectSelectedRows() {
    const result = [];

    for (const row of this.getRows())
      if (row.tableRow && row.tableRow.selected)
        result.push(row);

    return result;
  }

  initializeActionCandidates(candidates, row) {
    for (const [name, entry] of this.collectRowPublishedActions(row))
      candidates.set(name, { action: entry.action, items: [entry.component] });
  }

  intersectActionCandidates(candidates, row) {
    const rowActions = this.collectRowPublishedActions(row);

    for (const name of [...candidates.keys()]) {
      const entry = rowActions.get(name);

      if (entry !== undefined)
        candidates.get(name).items.push(entry.component);
      else
        candidates.delete(name);
    }
  }

  buildActionsFromCandidates(candidates) {
    const result = [];

    for (const candidate of candidates.values())
      result.push({ name: candidate.action.name, caption: candidate.action.caption, execute: candidate.action.execute, items: candidate.items });

    return result;
  }

  collectRowPublishedActions(row) {
    const result = new Map();

    for (const cell of row.cells) {
      const component = this.findCellMainComponent(cell);

      if (component !== null) {
        for (const action of component.getPublishedActions())
          result.set(action.name, { action: action, component: component });
      }
    }

    return result;
  }

  findCellMainComponent(cell) {
    const elements = Array.from(cell.querySelectorAll("*"));
    const element = elements.find((candidate) => candidate.component !== undefined);
    return element !== undefined ? element.component : null;
  }

  getRowCount() {
    const rows = this.getRows();
    let count = 0;

    for (const row of rows) {
      const cell = row.cells[0];
      const checkBox = cell.getElementsByTagName("input")[0];

      if (checkBox != null && !checkBox.disabled)
        count++;
    }

    return count;
  }

  createSelector() {
    this.selectAll = null;

    if (this.rowSelectorEnabled) {
      const element =
        new DomPathQuery(this.tableElement)
          .getChild(WithTagName("THEAD"))
          .getChild(WithClass("Columns"))
          .getChild(WithClass("rowSelector"))
          .getChild(function (node) { return node.component !== undefined && node.component instanceof CheckBoxField })
          .getElement();

      if (element !== null) {
        this.selectAll = new Selector(element);
        this.selectAll.recalculate(this);
      }
    }
  }

  handleEvent(event) {
    if (event instanceof DataChangedEvent) {
      if (this.refreshMode === RefreshMode.ForSiblings)
        this.invalidate();
      else if (this.element.contains(event.field.element))
        this.invalidateScopes([TableRenderScope.Toolbar]);
    }
    else
      super.handleEvent(event);
  }

  handleError(error) {
    application.toastBox.addMessage(new ToastMessage(this.labels.FailedToLoadTable, "Warning"));
  }

  async validate() {
    this.invalidateScopes([TableRenderScope.Complete]);
  }

  loadSettings(element) {
    this.filterEnabled = element.dataset.FilterEnabled === "true";
    this.rowSelectorEnabled = element.dataset.RowSelectorEnabled === "true";
    this.useSiblingComponent = element.dataset.UseSiblingComponent === "true";
    this.renderTime = element.dataset.RenderTime;
    this.view = element.dataset.View;

    this.element.tabIndex = "0";
    this.renderStatus = false;
    this.lastSelected = null;
  }

  initializeComponents() {
    this.updatingCounter = new HtmlClassCounter(this.element, "Updating");
    this.busy = new HtmlClassSwitch(this.element, "Busy");
    this.expertMode = new HtmlClassSwitch(this.element, "Expert");

    this.requestQueue = new RequestQueue(this);
    this.previousQueueSize = 0;
    this.requestQueue.listeners.push((event) => {
      if (event instanceof QueueSizeChangedEvent) {
        while (this.previousQueueSize < event.size) {
          this.updatingCounter.increase();
          this.previousQueueSize++;
        }

        while (this.previousQueueSize > event.size) {
          this.updatingCounter.decrease();
          this.previousQueueSize--;
        }

        this.busy.setStatus(this.requestQueue.requests.find((request) => { return request.blocking; }) !== undefined);
      }
    });

    this.invalidScopes = new Set();
    this.columnIdentifierIndexMapping = new Object();
    this.onChanged = null;

    this.filterTimer = new Timer(250);
    this.filterText = "";
    this.updateFilterVisibility();

    this.cellActionsEnabled = this.determineCellActionsEnabled();

    this.attachRowEventHandlers();
    this.createSelector();
  }

  initializeCells() {
    const header = this.getColumnsRows()[0];
    let columnIndex = 0;

    for (const column of header.cells) {
      const caption = new DomQuery(column).getDescendant(WithClass("Cell"));

      for (const row of this.getRows()) {
        if (caption !== null) {
          const node = caption.cloneNode(true);
          row.cells[columnIndex].prepend(node);
        }
      }

      columnIndex += column.colSpan;
    }

    for (const row of this.getRows()) {
      const subs = new DomQuery(row).getChildren(WithClass("Sub"));

      for (const sub of subs) {
        const field = sub.childNodes[sub.childNodes.length - 2];

        if (field.nodeType === Node.ELEMENT_NODE) {
          if ("Value" in field.dataset) {
            if (field.dataset.Value.length === 0)
              sub.classList.add("Empty");
          }
          else if (field.innerText.length === 0)
            sub.classList.add("Empty");
        }
        else if (field.nodeType === Node.TEXT_NODE && field.textContent.length === 0)
          sub.classList.add("Empty");
      }

      const last = subs.findLast((element) => !element.classList.contains("Empty"));

      if (last !== undefined)
        last.classList.add("Last");
    }
  }

  determineElements() {
    const query = new DomQuery(this.element);

    this.contents = query.getChild(WithClass("Contents"));
    this.tableElement = this.determineTableElement();

    this.toolbar = new Toolbar(query.getChild(WithClass("Toolbar")), this);

    this.navigation = new Navigation(query.getChild(WithClass("NavigationBar")));
    this.layoutPanel = new LayoutPanel(query.getChild(WithClass("Layout")), this);
    this.labels = new Labels(query.getChild(WithClass("Labels")));
    this.activeFilters = query.getChild(WithClass("ActiveFilters"));
  }

  bind() {
    this.handleInitialRenderTime();
  }

  toggleExpertMode() {
    this.expertMode.toggle();
  }

  get uri() {
    return this.element.dataset.Uri;
  }

  set uri(uri) {
    this.element.dataset.Uri = uri;
  }
}

export class LayoutPanel extends WebPageComponent {
  constructor(element, table) {
    super(element);

    this.table = table;
    this.visible = new HtmlClassSwitch(element, "Visible");
    this.sourceColumn = null;
    this.dropped = false;
    this.projection = null;
    this.predefined = null;
    this.layoutDropDown = null;
    this.shareButton = null;
    this.confirmationWindow = null;

    this.attachEventHandlers();
    this.bind();
  }

  createColumnDragStartHandler(column) {
    return (event) => {
      this.sourceColumn = column;
      this.sourceIndex = this.columns.indexOf(column);

      column.classList.add("Moving");
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
      event.dataTransfer.setData("text/plain", "move-column");
    };
  }

  createColumnDragEndHandler(column) {
    return (event) => {
      if (!this.dropped) {
        if (this.columns.indexOf(column) < this.sourceIndex)
          this.moveColumn(this.sourceIndex + 1);
        else
          this.moveColumn(this.sourceIndex);
      }

      this.sourceColumn.classList.remove("Moving");
      this.sourceColumn = null;
      this.dropped = false;
    };
  }

  createOnDragOverHandler() {
    return (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";

      this.moveColumn(this.determineTargetIndex(event.target.offsetTop + event.offsetY));
    };
  }

  createOnDragLeaveHandler(column) {
    return (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    };
  }

  createOnDragExitHandler(column) {
    return (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    };
  }

  createOnDragEnterHandler(column) {
    return (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    };
  }

  createOnDropHandler(column) {
    return (event) => {
      event.preventDefault();
      this.dropColumn();
    };
  }

  getColumnMiddle(column) {
    return column.offsetTop + column.offsetHeight / 2;
  }

  determineTargetIndex(offsetY) {
    let result = 0;

    while (result < this.columns.length && this.getColumnMiddle(this.columns[result]) < offsetY)
      result++;

    return result;
  }

  moveColumn(targetIndex) {
    const sourceIndex = this.columns.indexOf(this.sourceColumn);

    if (targetIndex !== sourceIndex && targetIndex !== sourceIndex + 1) {
      const targetColumn = this.columns[targetIndex];

      this.projection.insertBefore(this.sourceColumn, targetColumn);
      this.columns = new DomQuery(this.projection).getChildren(WithClass("Column"));
    }
  }

  dropColumn() {
    this.updateProjection();
    this.dropped = true;
  }

  updateProjection() {
    this.table.sendCommandRequest("<Projection>" + this.getCurrentProjectionAsXml() + "</Projection>");
  }

  attachEventHandlers() {
    this.projection = new DomQuery(this.element).getDescendant(WithClass("Projection"));
    this.projection.ondragover = this.createOnDragOverHandler();
    this.projection.ondragleave = this.createOnDragLeaveHandler();
    this.projection.ondragenter = this.createOnDragEnterHandler();
    this.projection.ondragexit = this.createOnDragExitHandler();
    this.projection.ondrop = this.createOnDropHandler();

    this.columns = new DomQuery(this.projection).getChildren(WithClass("Column"));

    for (const column of this.columns) {
      const checkBox = column.childNodes[0].component;
      checkBox.addEventListener(
        "change",
        (event) => {
          this.updateProjection();
        }
      );

      column.ondragstart = this.createColumnDragStartHandler(column);
      column.ondragend = this.createColumnDragEndHandler(column);
      column.draggable = true;
    }
  }

  handleLayoutDropDownChanged() {
    this.table.sendCommandRequest("<SelectLayout Name=\"" + escapeXMLAttributeValue(this.layoutDropDown.getValue()) + "\"/>");
    this.close();
  }

  saveLayout() {
    const layoutName = this.layoutName.getValue();
    this.table.sendCommandRequest("<SaveLayout Name=\"" + escapeXMLAttributeValue(layoutName) + "\"/>");
    this.close();

    application.toastBox.addMessage(new ToastMessage(this.table.labels.ActionExecutedSuccessfully, "Success"));
  }

  shareLayout() {
    const layoutName = this.layoutName.getValue();
    this.table.sendCommandRequest("<ShareLayout Name=\"" + escapeXMLAttributeValue(layoutName) + "\"/>");
    this.close();

    application.toastBox.addMessage(new ToastMessage(this.table.labels.ActionExecutedSuccessfully, "Success"));
  }

  createOnLayoutDropDownChangedHandler() {
    return (event) => {
      this.handleLayoutDropDownChanged();
    };
  }

  bind() {
    this.closeButton = new DomQuery(this.element).getDescendant(WithClass("Close"));
    this.closeButton.addEventListener("click", () => { this.close(); });

    this.predefined = new DomQuery(this.element).getDescendant(WithClass("Predefined"));

    this.layoutDropDown = this.getDescendant(new ComponentWithName("Layout"));
    this.layoutDropDown.addEventListener("change", this.createOnLayoutDropDownChangedHandler());

    this.layoutName = this.getDescendant(new ComponentWithName("Name"));
    this.saveButton = new DomQuery(this.element).getDescendant(WithClass("Save"));
    this.shareButton = new DomQuery(this.element).getDescendant(WithClass("Share"));

    if (this.saveButton !== null)
      this.saveButton.addEventListener("click", (event) => this.saveLayout());

    if (this.shareButton !== null)
      this.shareButton.addEventListener("click", (event) => this.shareLayout());
  }

  getCurrentProjectionAsXml() {
    let result = "";

    for (const column of this.columns)
      result += "<Column Identifier=\"" + column.dataset.Identifier + "\" Index=\"" + column.dataset.Index + "\" Visible=\"" + column.childNodes[0].component.getValue() + "\"/>";

    return result;
  }

  open() {
    this.visible.setStatus(true);

    this.clickOutsideListener = connectClickOutsideListener(
      this.element.childNodes[0],
      () => {
        this.close();
      }
    );
  }

  close() {
    this.visible.setStatus(false);
    removeClickOutsideListener(this.clickOutsideListener);
  }
}

function Navigation(element) {
  this.determineElements = function () {
    const query = new DomQuery(this.element);

    this.information = query.getChild(WithClass("Information"));
    this.loadRows = query.getChild(WithClass("LoadRows"));
    this.nextPage = query.getChild(WithClass("NextPage"));
    this.previousPage = query.getChild(WithClass("PreviousPage"));

    this.determineButtonStates();
  }

  this.determineButtonStates = function () {
    if (this.loadRows !== null)
      this.loadRows.disabled = this.total === null || this.size >= this.total || this.size === 1000;
    else {
      this.nextPage.disabled = this.total === null || this.offset + this.size >= this.total;
      this.previousPage.disabled = this.offset === 0;
    }
  }

  this.increasePageSize = function (pageSize) {
    if (pageSize === this.initialPageSize)
      return 250;
    else if (pageSize < 1000)
      return pageSize + 250;
    else
      return pageSize;
  }

  this.update = function (xml) {
    this.information.innerText = xml.getAttribute("Label");
    this.offset = xml.getAttribute("Offset");
    this.initialPageSize = xml.getAttribute("InitialPageSize");
    this.size = xml.getAttribute("PageSize");

    if (xml.hasAttribute("Total"))
      this.total = xml.getAttribute("Total");

    this.determineButtonStates();
  }

  Object.defineProperty(this, "initialPageSize", {
    get: function () {
      return parseInt(this.information.getAttribute("data--initial-page-size"));
    },
    set: function (size) {
      this.information.setAttribute("data--initial-page-size", size);
    }
  });

  Object.defineProperty(this, "offset", {
    get: function () {
      return parseInt(this.information.getAttribute("data--offset"));
    },
    set: function (offset) {
      this.information.setAttribute("data--offset", offset);
    }
  });

  Object.defineProperty(this, "size", {
    get: function () {
      return parseInt(this.information.getAttribute("data--page-size"));
    },
    set: function (size) {
      this.information.setAttribute("data--page-size", size);
    }
  });

  Object.defineProperty(this, "total", {
    get: function () {
      if (this.information.hasAttribute("data--total"))
        return parseInt(this.information.getAttribute("data--total"));
      else
        return null;
    },
    set: function (total) {
      this.information.setAttribute("data--total", total);
    }
  });

  this.element = element;
  this.determineElements();
}

export class Selector extends WebPageComponent {
  constructor(element) {
    super(element);

    this.checkBox = element.component;

    this.count = document.createElement("span");
    this.count.classList.add("Count");

    this.element.parentNode.appendChild(this.count);
  }

  recalculate(table) {
    const rowCount = table.getRowCount();
    const selected = table.getSelectedRowCount();

    if (selected > 0)
      this.count.innerHTML = selected;
    else
      this.count.innerHTML = "";

    this.checkBox.input.indeterminate = false;

    if (rowCount == 0 || selected == 0)
      this.checkBox.input.checked = false;
    else if (selected < rowCount)
      this.checkBox.input.indeterminate = true;
    else
      this.checkBox.input.checked = true;
  }
}

export class GroupingTable extends WebPageComponent {
  constructor(element) {
    super(element);

    this.determineElements();
  }

  expandOrCollapse(element) {
    const classes = new HtmlClasses(element);

    classes.toggle("collapsed");
    classes.toggle("expanded");
  }

  createOnClickHandler(item) {
    return () => {
      const body = new DomQuery(item).getAncestor(WithClass("expandable"));
      this.expandOrCollapse(body);
    };
  }

  attachItemHandlers(element) {
    element.onclick = this.createOnClickHandler(element);
  }

  attachItemsHandlers(elements) {
    for (const element of elements)
      this.attachItemHandlers(new DomQuery(element).getChild(WithClass("category")));
  }

  determineElements() {
    this.attachItemsHandlers(new DomQuery(this.element).getDescendants(WithClass("expandable")));
  }
}

export class MasterTableRow extends WebPageComponent {
  constructor(element) {
    super(element);

    this.detailsElement = element.nextSibling;
    this.determineElements();
  }

  expandOrCollapse(element, event) {
    const source = event.getSource();

    if (shouldHandleMouseClick(element, source)) {
      const classes = new HtmlClasses(this.detailsElement);
      classes.toggle("Collapsed");
      classes.toggle("Expanded");
    }
  }

  createOnClickHandler(item) {
    return (event) => {
      this.expandOrCollapse(item, getEvent(event));
    };
  }

  attachItemHandler(element) {
    element.onclick = this.createOnClickHandler(element);
  }

  determineElements() {
    this.attachItemHandler(this.element);
  }
}

export class FilterField extends WebPageComponent {
  constructor(element, handler, id) {
    super(element);

    this.handler = handler;
    this.id = id;

    this.button = element.childNodes[0];
    this.button.tabIndex = -1;

    this.target = element.childNodes[1].component;
    this.form = element.childNodes[2];

    this.fields = null;

    this.filterMode = null;
    this.popup = createPopUp(this.form, this.element, true, this.button, 0, this.button);

    this.determineElements();
    this.attachEventHandlers();

    this.initialFilterText = this.getFilterText();
    this.initialFilterMode = this.getFilterMode();
  }

  attachEventHandlers() {
    this.element.onkeydown = (event) => {
    };

    this.target.addEventListener("input", (event) => { this.handler(); });
    this.button.onclick = (event) => { this.toggleForm(event); };

    for (const field of this.fields)
      field.onchange = (event) => { this.handler(); };
  }

  determineElements() {
    this.fields = new DomQuery(this.form).getDescendants(WithTagName("INPUT"));
  }

  getId() {
    return this.id;
  }

  getFilterText() {
    const value = this.target.getValue();
    return value !== null ? value : "";
  }

  getFilterMode() {
    for (const field of this.fields) {
      if (field.checked)
        this.filterMode = field.getAttribute("value");
    }

    return this.filterMode;
  }

  toggleForm(event) {
    if (this.popup.isOpen())
      this.popup.close();
    else {
      this.popup.open();
      this.fields[0].focus();
    }

    event.stopPropagation();
  }
}

let openCellActions = null;

export class TableCellActions {
  constructor(table, cell) {
    this.table = table;
    this.cell = cell;
    this.popup = null;
    this.pointerLeft = null;
    this.selected = new HtmlClassSwitch(cell, "CellActionsSelected");

    this.attachEventHandlers();
  }

  attachEventHandlers() {
    this.cell.addEventListener(
      "pointerenter",
      (event) => {
        if (event.pointerType === "mouse") {
          this.pointerLeft = event.clientX;
          this.open();
        }
      }
    );

    this.cell.addEventListener("pointerleave", (event) => { this.close(); });
  }

  open() {
    if (this.popup === null) {
      const content = this.createContent();

      if (content !== null)
        this.popup = createPopUp(content, this.cell, false, this.cell, 400);
    }

    if (this.popup !== null) {
      if (openCellActions !== null && openCellActions !== this)
        openCellActions.close();

      this.popup.open(this.pointerLeft);

      this.selected.setStatus(true);
      openCellActions = this;
    }
  }

  close() {
    if (this.popup !== null)
      this.popup.close();

    this.selected.setStatus(false);
  }

  createContent() {
    let result = null;
    const actions = this.createActions();

    if (actions.length > 0) {
      result = document.createElement("div");
      result.className = "CellActions";

      for (const action of actions)
        result.appendChild(action);
    }

    return result;
  }

  createActions() {
    const result = new Array();
    const mode = this.determineFilterMode();

    for (const link of this.createLinks())
      result.push(link);

    if (this.getCellValue().length > 0)
      result.push(this.createCopyButton());

    if (mode !== null && this.cell.dataset.FilterValue !== undefined)
      result.push(this.createFilterButton(mode, this.cell.dataset.FilterValue));

    return result;
  }

  getCellValue() {
    const values = new Array();

    for (const node of this.cell.childNodes)
      if (!this.isDecoration(node)) {
        const value = (node instanceof HTMLElement ? node.innerText : node.textContent).trim();

        if (value.length > 0)
          values.push(value);
      }

    return values.join(" ");
  }

  isDecoration(node) {
    return (nodeHasClass(node, "Caption") && nodeHasClass(node, "Cell")) || nodeHasClass(node, "Problems");
  }

  createLinks() {
    const result = new Array();
    const suggest = new DomQuery(this.cell).getDescendant(WithClass("Suggest"));

    if (suggest !== null) {
      for (const card of suggest.querySelectorAll("tr.Card")) {
        if (new DomQuery(card.parentNode).getAncestor(WithClass("Suggest")) === suggest) {
          const main = new DomQuery(card).getChild(WithClass("Main"));

          if (main !== null && main.dataset.Uri !== undefined)
            result.push(this.createLink(main));
        }
      }
    }

    return result;
  }

  createLink(main) {
    const link = document.createElement("a");

    link.className = "Open";
    link.href = main.dataset.Uri;
    link.title = main.textContent.replace(/\s+/g, " ").trim();
    link.addEventListener("click", (event) => { this.close(); });

    return link;
  }

  createCopyButton() {
    const button = document.createElement("button");

    button.className = "Copy";
    button.title = this.table.labels.Copy.textContent;
    button.addEventListener("click", (event) => { this.copy(this.getCellValue()); });

    return button;
  }

  createFilterButton(mode, value) {
    const button = document.createElement("button");

    button.className = "Filter";
    button.title = this.table.labels.FilterOnThisValue.textContent;
    button.addEventListener("click", (event) => { this.filter(mode, value); });

    return button;
  }

  determineFilterMode() {
    let result = null;
    const column = this.cell.dataset.Column;

    if (column !== undefined) {
      for (const row of this.table.getFilterRows()) {
        for (const filterCell of row.cells) {
          if (filterCell.dataset.Identifier === column && filterCell.dataset.FilterMode !== undefined)
            result = filterCell.dataset.FilterMode;
        }
      }
    }

    return result;
  }

  copy(value) {
    this.close();

    navigator.clipboard.writeText(value)
      .then(() => { application.toastBox.addMessage(new ToastMessage(this.table.labels.ValueCopiedSuccessfully, "Success")); })
      .catch((error) => { application.toastBox.addMessage(new ToastMessage(this.table.labels.FailedToCopyValue, "Error")); });
  }

  filter(mode, value) {
    this.close();
    this.table.filterColumn(this.cell.dataset.Column, mode, value);
  }
}

interactivityRegistration.register("Table", function (element) { return new Table(element); });
interactivityRegistration.register("grouping", function (element) { return new GroupingTable(element); });
interactivityRegistration.register("Master", function (element) { return new MasterTableRow(element); });
